'use client';

import { useEffect, useRef, useState } from 'react';
import { IconChevronDown } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { toTitleCase } from '@/utils/textTransform';
import { useOwner } from '../../context/OwnerProvider';
import { contactVerdict, displayRole, verdictInputFromOwner } from '@/lib/seo/verdict';

export function OwnerAbout() {
  const { owner } = useOwner();
  const { contactSummary, name } = owner;

  /**
   * Verdict paragraph (GEO 2.2) — a self-contained factual sentence an
   * answer engine can lift whole, rather than assembling from cards.
   *
   * Two things gate it. `verdictInputFromOwner` returns null unless every
   * figure is present (see `canRenderVerdict`), and the component's
   * existing `!contactSummary` early-return means a verdict never appears
   * without a summary beneath it — summary generation is still rolling
   * out, and a lone verdict reads as a truncated page.
   */
  const verdictInput = verdictInputFromOwner(owner);
  // `toTitleCase` is only safe on a real string — the section title below
  // already guards it the same way. A null name here would throw and take
  // the page down.
  const verdict = verdictInput
    ? contactVerdict(name ? toTitleCase(name) : null, displayRole(owner.roles), verdictInput)
    : null;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Measure whether the clamped content actually overflows. Runs on mount
  // and on window resize (viewport width affects line count). Only shows
  // "View more" when there's actually more to see.
  useEffect(() => {
    if (!contactSummary) return;

    const check = () => {
      const el = contentRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
    // `verdict` is in the deps because it lands with the payload and
    // changes the clamped height — without it the overflow check runs
    // against summary-only content and "View more" can be wrong.
  }, [contactSummary, verdict]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!contactSummary) {
    return null;
  }

  const title = `About ${name ? toTitleCase(name) : 'this contact'}`;
  const showToggle = isOverflowing || isExpanded;

  return (
    <div>
      <SectionHeader title={title} />

      <div className={cn('flex flex-col items-start py-0 w-full', 'px-2', 'md:px-4')}>
        {/*
          Verdict and summary share one clamped block so "View more" governs
          both together.

          Joined as a single text node with a blank line rather than two
          `<p>` elements: Tailwind's `line-clamp-*` relies on
          `-webkit-line-clamp` with `display: -webkit-box`, which counts
          lines unreliably across separate block children. One text node
          plus `whitespace-pre-line` clamps predictably.
        */}
        <div
          ref={contentRef}
          className={cn(
            'text-body-2 text-primary w-full whitespace-pre-line',
            !isExpanded && 'line-clamp-5',
          )}
        >
          {verdict ? `${verdict}\n\n${contactSummary}` : contactSummary}
        </div>

        {showToggle && (
          <button
            onClick={toggleExpanded}
            className="flex gap-1 items-center justify-center px-0 py-2 mt-2"
          >
            <span className="text-body-2-high text-accent text-center whitespace-nowrap">
              {isExpanded ? 'View less' : 'View more'}
            </span>
            <div className="w-3 h-3 flex-shrink-0">
              <IconChevronDown
                className={cn(
                  'w-full h-full icon-accent transition-transform duration-200',
                  isExpanded && 'rotate-180',
                )}
              />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
